import { useRef } from "react";
import PageSEO from "@/components/PageSEO";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  MapPin, Zap, Calculator, TrendingUp, Shield, IndianRupee,
  Check, ArrowRight, ChevronRight, Home, Building2, HardHat, Briefcase, Cpu,
} from "lucide-react";

const F = {
  950: "#2d2219", 900: "#3a2e28", 800: "#504136", 700: "#5e4e41", 600: "#6e5c4e",
  500: "#689689", 400: "#83E8BA", 300: "#8ab0a8",
  200: "#B2E6D4", 100: "#c8ede4", 50: "#edf8f4",
  e500: "#A49E8D", e400: "#8c8678", e300: "#c0b9ac", e100: "#f0ede8",
  m600: "#2d4f4b", m500: "#3d6b65", m400: "#4a7068", m300: "#5e8480",
  c100: "#f5f2ec", c200: "#e8e3d8", c50: "#faf9f6",
};

function Reveal({ children, className, delay = 0, y = 30 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const FEATURES = [
  {
    icon: MapPin, color: F[500], title: "AI Site Classification",
    desc: "Drop in land dimensions and state. AI classifies the site, surfaces opportunities, and flags constraints before you invest.",
    bullets: ["Urban, highway & semi-urban analysis", "State tariff & DISCOM lookup", "Grid capacity & risk flags"],
  },
  {
    icon: Zap, color: F.e400, title: "Optimised Layout Design",
    desc: "Canvas-based drag-and-drop layout with automatic charger, parking, and solar placement tuned for maximum coverage.",
    bullets: ["Multi-shape land support", "Auto area-coverage calculation", "Solar + EV co-placement"],
  },
  {
    icon: Calculator, color: F[400], title: "Transparent CAPEX Model",
    desc: "Every rupee itemised — civil, electrical, chargers, solar, buildings, and miscellaneous — with real-time totals.",
    bullets: ["State-adjusted cost multipliers", "Per-charger cost breakdown", "CAPEX vs subsidy netting"],
  },
  {
    icon: TrendingUp, color: F.e500, title: "ROI & Scenario Analysis",
    desc: "10-year revenue projection with interactive sliders for utilisation, price per session, and CAPEX. Payback and IRR update live.",
    bullets: ["Payback period & IRR", "Side-by-side scenario comparison", "Revenue sensitivity analysis"],
  },
  {
    icon: Shield, color: F.m400, title: "Compliance Checklist",
    desc: "India-specific compliance across electrical, fire, safety, and environmental categories — merged from central and state regulations.",
    bullets: ["29 states covered", "Mandatory vs recommended flagged", "Progressive tick-off as you build"],
  },
  {
    icon: IndianRupee, color: F[300], title: "Government Subsidy Finder",
    desc: "FAME III, PM e-DRIVE, state schemes, and MSME financing — auto-matched to your project with net CAPEX after subsidies.",
    bullets: ["Total subsidy calculated instantly", "Application links included", "MSME & DISCOM schemes"],
  },
];

const STEPS = [
  { step: 1, title: "Enter Site Details", detail: "Define your location, state, land dimensions, and intended use-case. The platform auto-populates state tariffs, compliance rules, and applicable government incentives." },
  { step: 2, title: "AI Generates Layout", detail: "AI classifies your site, recommends charger types and quantities, and generates an optimised layout — in under 30 seconds." },
  { step: 3, title: "Review & Customise", detail: "Use the interactive canvas to adjust placements, tweak ROI sliders, and model different scenarios until the numbers stack up." },
  { step: 4, title: "Export Report", detail: "One click generates a polished PDF with layout, CAPEX breakdown, ROI projections, and compliance checklist — investor-ready." },
];

const AUDIENCES = [
  { icon: Home,      role: "Landowners",        desc: "Check suitability, model lease vs ownership revenue, understand permits." },
  { icon: Building2, role: "Infra Developers",  desc: "Full CAPEX breakdown, ROI & payback, investor-ready feasibility reports." },
  { icon: HardHat,   role: "EPC Contractors",   desc: "Optimised layouts, itemised civil & electrical costs, compliance flags." },
  { icon: Briefcase, role: "Consultants",        desc: "What-if scenario sliders, branded PDF in one click, reusable templates." },
  { icon: Cpu,       role: "OEMs",              desc: "Charger type recommendations, grid load & solar sizing, pre-sales data." },
];

export default function Planner() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: F.c100, fontFamily: "Manrope, sans-serif" }}>
      <PageSEO
        title="EV Charging Station Planner for India"
        description="Step-by-step EV charging station planner for India. Enter your site details, charger requirements and get an instant layout, CAPEX estimate and ROI projection."
        path="/planner"
      />
      {/* Header */}
      <header
        className="sticky top-0 z-40 backdrop-blur-md"
        style={{ backgroundColor: F[800] + "f2", borderBottom: `1px solid ${F[700]}` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <button
            onClick={() => navigate("/")}
            style={{ color: F[200], fontFamily: "Chivo, sans-serif", fontWeight: 700, fontSize: "1rem" }}
          >
            EV Hub Studio
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/roi-calculator")}
              className="text-sm hidden sm:block transition-colors"
              style={{ color: F[300] }}
            >
              ROI Calculator
            </button>
            <button
              onClick={() => navigate("/try")}
              className="text-sm px-4 py-1.5 rounded-lg font-semibold transition-colors"
              style={{ backgroundColor: F[500], color: "#fff" }}
            >
              Try Free
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 text-center">
        <Reveal>
          <span
            className="inline-block text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-5"
            style={{ backgroundColor: F[500] + "20", color: F[500] }}
          >
            EV Charging Station Planner
          </span>
          <h1
            className="text-4xl sm:text-5xl font-bold mb-5 leading-tight"
            style={{ color: F[800], fontFamily: "Chivo, sans-serif" }}
          >
            Plan your EV charging station<br />
            <span style={{ color: F[500] }}>in minutes, not months.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-lg mb-8 max-w-2xl mx-auto leading-relaxed" style={{ color: F.m400 }}>
            India's most comprehensive EV charging station planning tool. Generate CAPEX models,
            ROI projections, site layouts, compliance checklists, and subsidy calculations —
            all in one platform, free to use.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={() => navigate("/try")}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all hover:opacity-90"
              style={{ backgroundColor: F[500], color: "#fff", fontFamily: "Chivo, sans-serif" }}
            >
              Try the Planner <ArrowRight size={17} />
            </button>
            <button
              onClick={() => navigate("/roi-calculator")}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold border transition-all hover:opacity-80"
              style={{ borderColor: F[200], color: F[800], backgroundColor: "#fff" }}
            >
              ROI Calculator <ChevronRight size={17} />
            </button>
          </div>
        </Reveal>
      </section>

      {/* Features grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <Reveal>
          <h2
            className="text-2xl font-bold text-center mb-10"
            style={{ color: F[800], fontFamily: "Chivo, sans-serif" }}
          >
            Everything you need to evaluate an EV charging site
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.06}>
              <div
                className="rounded-2xl border p-6 h-full"
                style={{ backgroundColor: "#fff", borderColor: F[200] }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: f.color + "22" }}
                >
                  <f.icon size={20} style={{ color: f.color }} />
                </div>
                <h3
                  className="font-bold mb-2"
                  style={{ color: F[800], fontFamily: "Chivo, sans-serif" }}
                >
                  {f.title}
                </h3>
                <p className="text-sm mb-3 leading-relaxed" style={{ color: F.m400 }}>{f.desc}</p>
                <ul className="space-y-1.5">
                  {f.bullets.map(b => (
                    <li key={b} className="flex items-center gap-2 text-sm" style={{ color: F.m500 }}>
                      <Check size={12} strokeWidth={3} style={{ color: F[500], flexShrink: 0 }} />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="py-16" style={{ backgroundColor: F[900] }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2
              className="text-2xl font-bold text-center mb-10"
              style={{ color: F[200], fontFamily: "Chivo, sans-serif" }}
            >
              How the planner works
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5">
            {STEPS.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.08}>
                <div
                  className="rounded-xl p-5"
                  style={{ backgroundColor: F[800], border: `1px solid ${F[700]}` }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center mb-3 text-sm font-bold"
                    style={{ backgroundColor: F[500], color: "#fff", fontFamily: "Chivo, sans-serif" }}
                  >
                    {s.step}
                  </div>
                  <h3
                    className="font-bold mb-1.5"
                    style={{ color: F[100], fontFamily: "Chivo, sans-serif" }}
                  >
                    {s.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: F[300] }}>{s.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Reveal>
          <h2
            className="text-2xl font-bold text-center mb-10"
            style={{ color: F[800], fontFamily: "Chivo, sans-serif" }}
          >
            Built for everyone in the EV charging value chain
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.role} delay={i * 0.06}>
              <div
                className="rounded-2xl border p-5 text-center h-full"
                style={{ backgroundColor: "#fff", borderColor: F[200] }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3"
                  style={{ backgroundColor: F[50] }}
                >
                  <a.icon size={20} style={{ color: F[500] }} />
                </div>
                <p className="font-bold text-sm mb-1.5" style={{ color: F[800], fontFamily: "Chivo, sans-serif" }}>
                  {a.role}
                </p>
                <p className="text-xs leading-relaxed" style={{ color: F.m400 }}>{a.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-16"
        style={{ backgroundColor: F[50], borderTop: `1px solid ${F[200]}` }}
      >
        <div className="max-w-2xl mx-auto px-4 text-center">
          <Reveal>
            <h2
              className="text-2xl font-bold mb-3"
              style={{ color: F[800], fontFamily: "Chivo, sans-serif" }}
            >
              Ready to plan your site?
            </h2>
            <p className="mb-6 leading-relaxed" style={{ color: F.m400 }}>
              No sign-up required for the quick estimator. Create a free account to save
              projects, generate PDF reports, and access all features.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={() => navigate("/try")}
                className="flex items-center gap-2 px-7 py-3 rounded-xl font-semibold transition-all hover:opacity-90"
                style={{ backgroundColor: F[500], color: "#fff", fontFamily: "Chivo, sans-serif" }}
              >
                Start Planning Free <ArrowRight size={17} />
              </button>
              <button
                onClick={() => navigate("/site-selection")}
                className="flex items-center gap-2 px-7 py-3 rounded-xl font-semibold border transition-all hover:opacity-80"
                style={{ borderColor: F[300], color: F[800], backgroundColor: "#fff" }}
              >
                Site Selection Guide
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
